/** Copy of the blockStock page (Operations), ported from VentaHub. */
export const blockStock = {
  title: 'Block stock',
  description:
    'Block stock in inward boxes against a purchase order, so it is kept for that order until it is unblocked.',
  empty: 'No blocks to show',
  emptyHint: 'Nothing matches these filters. Use Block stock to block stock for a PO.',
  actions: {
    block: 'Block stock',
    addToBlock: 'Add to block',
    unblock: 'Unblock',
    checkAvailability: 'Check availability',
    download: 'Download',
    shiftStock: 'Shift / transfer stock',
    shiftByRetailerSku: 'Shift by retailer SKU',
    warehouseTransfer: 'Warehouse transfer',
    unblockAndShift: 'Unblock & shift',
    mailPacking: 'Mail to packing team',
  },
  chooseWarehouseFirst: 'Choose a warehouse first.',
  filters: {
    warehouse: 'Warehouse',
    text: 'PO code',
    sku: 'SKU code',
    retailerSku: 'Retailer SKU',
    codesPlaceholder: 'One or more, comma-separated',
    dates: 'Blocked on',
    void: 'Include unblocked',
  },
  columns: {
    poCode: 'PO code',
    warehouse: 'Warehouse',
    createdAt: 'Blocked on',
    units: 'Units',
    skuCount: 'SKUs',
    valid: 'Status',
    actions: 'Actions',
  },
  status: {
    blocked: 'Blocked',
    unblocked: 'Unblocked',
  },
  open: 'Open {{code}}',
  addTo: 'Add to block {{code}}',
  unblockOne: 'Unblock {{code}}',
  downloadLines: 'Download lines of {{code}}',
  lines: {
    title: 'Blocked lines',
    description: 'The SKUs and boxes this block holds.',
    empty: 'This block holds nothing.',
    columns: {
      skuCode: 'SKU code',
      skuName: 'SKU name',
      boxCode: 'Box code',
      quantity: 'Units',
      expiryDate: 'Expiry',
    },
  },
  editor: {
    skuCode: 'SKU code',
    boxCode: 'Box code',
    quantity: 'Units',
  },
  form: {
    blockHint:
      'Choose the warehouse and PO, then the units to block from each box. Only inward boxes can be blocked; earliest expiry is used first.',
    addTitle: 'Add to block {{code}}',
    addHint:
      'Units to add to this block in {{warehouse}}. They are added to what it already holds.',
    noLines: 'Add at least one line.',
    badLine: 'Line {{line}} needs a SKU code, a box code and a whole number of units.',
    needWarehouseAndPo: 'Choose a warehouse and enter the PO code.',
    needPo: 'Enter the PO code.',
    cancel: 'Cancel',
    working: 'Working…',
    blocked: 'Stock blocked for {{code}}.',
    added: 'Units added to the block for {{code}}.',
    downloadTemplate: 'Download template',
    uploadLines: 'Upload lines from a file',
    uploadEmpty: 'No lines found in the file.',
    uploadUnreadable: 'Could not read the file.',
    uploadLoaded_one: 'Loaded {{count}} line from the file.',
    uploadLoaded_other: 'Loaded {{count}} lines from the file.',
  },
  unblock: {
    title: 'Unblock {{code}}?',
    description:
      'Its {{units}} units become free for other orders straight away. The block stays in the history as unblocked.',
    done: 'Unblocked {{code}}.',
  },
  availability: {
    hint: 'See which inward boxes could cover each requirement right now, earliest expiry first. Nothing is blocked.',
    result: 'Availability',
    full: '{{po}} · {{sku}}: {{found}} of {{needed}} available',
    short: '{{po}} · {{sku}}: only {{found}} of {{needed}} available',
    close: 'Close',
    codeMode: {
      label: 'Name each product by',
      sku: 'SKU code',
      retailer: 'Retailer code',
    },
    retailerCode: 'Retailer code',
    upload: {
      divider: 'or upload',
      file: 'Requirements file',
      template: 'Download template',
      submit: 'Upload & check',
      working: 'Checking…',
    },
  },
  shift: {
    hint: 'Move units between boxes in one warehouse. The same SKU on both sides shifts it; a different one transfers the units to that SKU.',
    columns: {
      fromBox: 'From box',
      fromSku: 'From SKU',
      toBox: 'To box',
      toSku: 'To SKU',
    },
    done_one: 'Shifted {{count}} line.',
    done_other: 'Shifted {{count}} lines.',
  },
  shiftByRetailer: {
    hint: 'Move units to another box in one warehouse, each line naming its product by a retailer code (our own SKU code is taken too).',
    columns: {
      fromBox: 'From box',
      retailerSku: 'SKU code or retailer SKU',
      toBox: 'To box',
    },
  },
  warehouseTransfer: {
    hint: 'Move whole boxes out of this warehouse into another — each source box’s contents go into a new box at the destination.',
    toWarehouse: 'To warehouse',
    needTo: 'Choose the destination warehouse.',
    noLines: 'Add at least one box mapping.',
    badLine: 'Line {{line}} needs a box in each warehouse.',
    columns: {
      fromBox: 'From box',
      toBox: 'To box',
    },
    actions: {
      validate: 'Validate',
      transfer: 'Transfer',
    },
    boxes_one: '{{count}} box',
    boxes_other: '{{count}} boxes',
    units_one: '{{count}} unit',
    units_other: '{{count}} units',
    wouldMove: 'Would move {{boxes}} · {{units}}',
    moved: 'Moved {{boxes}} · {{units}}',
    result: {
      columns: {
        fromBox: 'From box',
        toBox: 'To box',
        units: 'Units',
        skuCount: 'SKUs',
        status: 'Status',
      },
      canMove: 'Can move',
      empty: 'No boxes.',
      cannotMove: 'Cannot move everything: fix the lines marked below.',
    },
  },
  unblockAndShift: {
    title: 'Unblock & shift {{code}}',
    hint: 'Release this block and move everything it holds to other boxes, in one step. The lines must account for every unit the block holds.',
    toBox: 'To box',
    done: 'Unblocked {{code}} and shifted its units.',
  },
  mailPacking: {
    title: 'Mail {{code}} to the packing team',
    hint: 'Send the block’s lines to the packing team as a spreadsheet attachment.',
    recipients: 'Recipients',
    recipientsPlaceholder: 'One or more addresses, comma-separated',
    needRecipients: 'Enter at least one address.',
    send: 'Send',
    done_one: 'Mailed {{count}} line to {{recipients}}.',
    done_other: 'Mailed {{count}} lines to {{recipients}}.',
  },
} as const
